import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, FileSearch, Users, Quote, BookOpen, Calendar } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ICONS = {
  '01': FileSearch,
  '02': Users,
  '03': ShieldCheck,
  '04': Quote,
  '05': Calendar,
};

const titleCase = (slug = '') =>
  slug.split('-').map((w) => (w === 'arpels' ? 'Arpels' : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');

export const MethodologyPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/methodology`);
        if (!cancelled) setData(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="methodology-page" className="bg-[#FAF9F6]">
      <SEO
        title="How We Verify — Editorial Methodology · Jewelry Refined"
        description="The five-step editorial process behind every Jewelry Refined article. Specialist authors, peer review, primary-source citation, and quarterly fact-check cycles."
        url="/methodology"
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'How We Verify', url: '/methodology' },
          ],
        }}
      />

      {/* Editorial Hero */}
      <section className="bg-[#111] text-white">
        <div className="container-editorial pt-20 pb-16 lg:pt-28 lg:pb-20">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">How We Verify</span>
          </nav>
          <p className="overline text-[#C69C6D] mb-6">Editorial Standards</p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            How We <span className="italic">Verify</span>
          </h1>
          <p className="font-heading italic text-xl lg:text-2xl text-white/85 max-w-3xl mb-6">
            {data.tagline}
          </p>
          <p className="text-white/70 leading-relaxed max-w-2xl text-lg">{data.intro}</p>
        </div>
      </section>

      {/* The Five-Step Process */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="mb-12 pb-6 border-b border-[#E5E0D8]">
            <p className="overline overline-gold mb-3">The Process</p>
            <h2 className="heading-editorial text-4xl lg:text-5xl">Five Steps. Every Article.</h2>
          </div>

          <div className="space-y-px bg-[#E5E0D8]">
            {data.process.map((step) => {
              const Icon = STEP_ICONS[step.step] || BookOpen;
              return (
                <div key={step.step} className="bg-white grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12">
                  <div className="lg:col-span-3 flex items-start gap-4">
                    <span className="font-heading text-5xl lg:text-6xl text-[#C69C6D] font-light leading-none">{step.step}</span>
                    <Icon className="w-6 h-6 text-[#111] mt-3" strokeWidth={1.25} />
                  </div>
                  <div className="lg:col-span-9">
                    <h3 className="font-heading text-2xl lg:text-3xl text-[#111] mb-4 font-light">{step.title}</h3>
                    <p className="text-[#555] leading-relaxed text-lg">{step.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialist Mapping */}
      <section className="section-editorial bg-white border-t border-[#E5E0D8]">
        <div className="container-editorial">
          <div className="mb-12 pb-6 border-b border-[#E5E0D8]">
            <p className="overline overline-gold mb-3">Specialist Authors</p>
            <h2 className="heading-editorial text-4xl lg:text-5xl">
              Every Cluster Has a <span className="italic">Specialist</span>
            </h2>
            <p className="text-[#555] mt-6 max-w-2xl text-lg leading-relaxed">
              Articles aren't written by whoever is free — they're written by the editor with the right credentials and decade of focus.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {data.specialists.map((s) => (
              <div
                key={s.cluster_slug}
                className="flex items-center justify-between py-4 border-b border-[#E5E0D8]"
                data-testid={`specialist-${s.cluster_slug}`}
              >
                <Link to={`/clusters/${s.cluster_slug}`} className="font-heading text-lg text-[#111] hover:text-[#C69C6D] transition-colors">
                  {titleCase(s.cluster_slug)}
                </Link>
                <Link to={`/authors/${s.author_name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '')}`} className="text-xs tracking-[0.15em] uppercase text-[#888] hover:text-[#111] transition-colors text-right">
                  {s.author_name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="max-w-3xl">
            <p className="overline overline-gold mb-3">Credentials</p>
            <h2 className="heading-editorial text-4xl lg:text-5xl mb-10">The Qualifications We Verify</h2>
            <ul className="space-y-4">
              {data.credentials.map((c, i) => (
                <li key={i} className="flex items-start gap-4 text-lg text-[#111] border-b border-[#E5E0D8] pb-4">
                  <ShieldCheck className="w-5 h-5 text-[#C69C6D] flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#111] text-white py-16">
        <div className="container-editorial text-center max-w-3xl mx-auto">
          <p className="overline text-[#C69C6D] mb-4">Read the Editorial Team</p>
          <h2 className="heading-editorial text-3xl lg:text-4xl text-white mb-8">
            Meet the <span className="italic">people</span> behind the verdicts
          </h2>
          <Link to="/about" className="btn-editorial btn-editorial-inverse inline-flex">Meet the Editors</Link>
        </div>
      </section>
    </div>
  );
};

export default MethodologyPage;
